import React, { useCallback } from "react";
import { TextField, RadioButtonGroup, Dropdown } from "..";
import useLocationFormatter from "hooks/useLocationFormatter";
import {
  COORDINATE_FORMATS,
  COORDINATE_LIMITS,
  CoordinatesValue,
  GpsData,
  useGpsCoordinatesForm,
} from "hooks/useGpsCoordinates";

interface GpsCoordinatesProps {
  value: GpsData;
  onChange: (data: GpsData) => void;
  editMode: boolean;
  elements: any[];
  className?: string;
  disabled?: boolean;
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  sectionId?: string;
  panelConfig?: any;
  panel?: any;
}

const GpsCoordinates: React.FC<GpsCoordinatesProps> = ({
  value = { CoordinatesFormat: "", Latitude: 0, Longitude: 0 },
  onChange,
  editMode,
  elements,
  className = "",
  disabled = false,
  onValidationChange,
  sectionId,
  panelConfig,
  panel = {},
}) => {
  const { formatLocation } = useLocationFormatter();
  const {
    handleFormatChange,
    handleCoordinateChange,
    handleDMSChange,
    formatChoices,
    coordinatesPlaceholders,
    convertDMS2DD,
  } = useGpsCoordinatesForm({
    value,
    onChange,
    disabled,
    elements,
    onValidationChange,
    formatLocation,
  });

  /* ---------- field renderer ---------- */
  const renderCoordinateField = useCallback(
    (coordinateType: "Latitude" | "Longitude") => {
      const isLat = coordinateType === "Latitude";
      const title = isLat ? "Latitude" : "Longitude";
      const directions = isLat ? ["N", "S"] : ["E", "W"];
      const current = value[coordinateType];
      const fmt = value.CoordinatesFormat;

      const isDMS = fmt === COORDINATE_FORMATS.DEGREES_MINUTES_SECONDS;
      const isDMM = fmt === COORDINATE_FORMATS.DEGREES_DECIMAL_MINUTES;
      const isDD = fmt === COORDINATE_FORMATS.DECIMAL_DEGREES;

      if (isDD) {
        return (
          <div className="col-span-6" key={coordinateType}>
            <TextField
              label={title}
              type="number"
              name={coordinateType.toLowerCase()}
              value={current as number}
              handleChange={(nv: any) => {
                if (nv === "") {
                  handleCoordinateChange(coordinateType, 0);
                  return;
                }
                const parsed = parseFloat(nv);
                if (!isNaN(parsed)) {
                  const lim = isLat
                    ? COORDINATE_LIMITS.LATITUDE
                    : COORDINATE_LIMITS.LONGITUDE;
                  if (parsed < lim.min || parsed > lim.max) return;
                }
                handleCoordinateChange(coordinateType, nv);
              }}
              editMode={editMode}
              placeholder={`Enter ${title} (${isLat ? "-90 to 90" : "-180 to 180"})`}
              required
              disabled={disabled}
              panel={panel || {}}
              element={{}}
              aria-label={`${title} in decimal degrees`}
            />
          </div>
        );
      }

      const coordValue = (current as CoordinatesValue) || {};

      return (
        <div
          className="col-span-6  items-end bg-bgPrimaryLight p-4 rounded-lg"
          key={coordinateType}
        >
          <p
            className={`w-full pb-1 text-left capitalize text-md font-Overpass ${editMode ? "font-semibold text-primary" : "font-regular text-primary"} dark:text-caption`}
          >
            {title}{" "}
            <span className="text-red-500" aria-label="required">
              *
            </span>
          </p>

          {editMode ? (
            <div
              className={`items-start lat-lng-fields gap-2 grid grid-cols-${isDMS ? "4" : "3"}`}
              role="group"
              aria-label={`${title} coordinates input`}
            >
              <Dropdown
                name="direction"
                label="Direction"
                choices={directions}
                value={coordValue?.direction}
                handleChange={(item: any) =>
                  handleDMSChange(coordinateType, "direction", item)
                }
                editMode={editMode}
                required
                disabled={disabled}
                error={!coordValue?.direction && "Direction is required"}
                touched
                labelSize="sm"
                panel={panelConfig || {}}
                element={{}}
                aria-label={`${title} direction`}
                choicesByUrl={null}
              />

              <TextField
                type="number"
                name="degrees"
                label="Degrees"
                value={
                  coordValue?.degrees ? parseFloat(coordValue.degrees) : ""
                }
                handleChange={(val: any) =>
                  handleDMSChange(coordinateType, "degrees", val)
                }
                editMode={editMode}
                placeholder={isLat ? "0–90" : "0–180"}
                required
                disabled={disabled}
                error={!coordValue?.degrees && "Degrees are required"}
                touched
                labelSize="sm"
                panel={panel}
                element={{}}
                aria-label={`${title} degrees`}
              />

              <TextField
                type="number"
                name="minutes"
                label="Minutes"
                value={
                  coordValue?.minutes
                    ? parseFloat(coordValue.minutes) +
                      (isDMM && coordValue?.seconds
                        ? parseFloat(coordValue.seconds) / 60
                        : 0)
                    : ""
                }
                handleChange={(val: any) =>
                  handleDMSChange(coordinateType, "minutes", val)
                }
                editMode={editMode}
                placeholder={isDMM ? "0–60 (decimal)" : "0–59"}
                required
                disabled={disabled}
                error={!coordValue?.minutes && "Minutes are required"}
                touched
                labelSize="sm"
                panel={panel}
                element={{}}
                aria-label={`${title} minutes`}
              />

              {isDMS && (
                <TextField
                  type="number"
                  name="seconds"
                  label="Seconds"
                  value={
                    coordValue?.seconds ? parseFloat(coordValue.seconds) : ""
                  }
                  handleChange={(val: any) =>
                    handleDMSChange(coordinateType, "seconds", val)
                  }
                  editMode={editMode}
                  placeholder="0–59"
                  required
                  disabled={disabled}
                  error={!coordValue?.seconds && "Seconds are required"}
                  touched
                  labelSize="sm"
                  panel={panel || {}}
                  element={{}}
                  aria-label={`${title} seconds`}
                />
              )}
            </div>
          ) : (
            <div className="relative flex items-center self-stretch justify-start">
              <p
                className="w-full max-w-full max-h-32 overflow-hidden text-overflow-ellipsis whitespace-pre-line text-[15px] font-Overpass text-left font-normal text-textAnswer dark:text-textMain"
                aria-label={`${title} value`}
              >
                {coordValue?.direction &&
                coordValue?.degrees &&
                coordValue?.minutes
                  ? convertDMS2DD(
                      coordValue.degrees,
                      coordValue.direction,
                      coordValue.minutes,
                      coordValue.seconds
                    )
                  : "Not set"}
              </p>
            </div>
          )}
        </div>
      );
    },
    [
      disabled,
      editMode,
      handleCoordinateChange,
      handleDMSChange,
      panel,
      panelConfig,
      value,
    ]
  );

  /* ---------- render ---------- */
  return (
    <div
      className={` col-span-12  ${className} ${disabled ? "opacity-50 pointer-events-none" : ""}`}
    >
      {/* Validation list (derived; no local state) */}
      {/* {validationErrors.length > 0 && (
        <div
          className="col-span-12 p-3 bg-red-50 border border-red-200 rounded-md"
          role="alert"
        >
          <h4 className="text-sm font-medium text-red-800 mb-1">
            Validation Errors:
          </h4>
          <ul className="text-xs text-red-700 space-y-1">
            {validationErrors.map((e, i) => (
              <li key={i}>• {e}</li>
            ))}
          </ul>
        </div>
      )} */}

      {/* Format selector */}
      <div className="!col-span-12">
        <RadioButtonGroup
          label="Coordinates Format"
          name="coordinatesFormat"
          editMode={editMode}
          value={value?.CoordinatesFormat}
          handleChange={handleFormatChange}
          data={formatChoices}
          required
          disabled={disabled}
          dataPlaceholders={coordinatesPlaceholders}
          panel={panelConfig || {}}
          element={formatChoices[0] || {}}
          aria-label="Select coordinate format"
        />
      </div>

      {/* Fields */}
      <div className="grid grid-cols-12 gap-4 mt-4">
        {renderCoordinateField("Latitude")}
        {renderCoordinateField("Longitude")}
      </div>
    </div>
  );
};

export default React.memo(GpsCoordinates);
